import React, { useState } from "react";
import { Form, Flex, Card, Button, message, List, Select } from "antd";
import { Location } from "../../../types/location";
import SalonSVG from "../../../assets/teamMember/SalonSVG";

import { DownOutlined } from "@ant-design/icons";
import EditSVG from "../../../assets/teamMember/EditSVG";
import { editTeamMember } from "../../../network/teamMember";
import { queryKeys } from "../../../services/react-query/queryKeys";
import queryClient from "../../../services/react-query/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAllLocations } from "../../../network/location";
import LocationCheckSVG from "../../../assets/teamMember/LocationCheckSVG";
import FolderLock from "../../../assets/teamMember/FolderLock";
import SettingsSVG from "../../../assets/teamMember/SettingsSVG";
import {
  getFormatedTeamMemberPermission,
  localizedTeamMemberPermission,
} from "../../../enum/teamMemberPermission";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

const Permission: React.FC<{ teamMemberData: any }> = ({ teamMemberData }) => {
  const intl = useIntl();
  const { currentLang } = useSelector(
    (state: { user: { currentLang: "en" | "ar" } }) => {
      return {
        currentLang: state.user.currentLang,
      };
    }
  );
  const PermissionCard: React.FC<{
    item: Location & { name: { ar: string; en: string } };
    index: number;
    editing: boolean;
    handleRemoveLocation: (locationId: number) => void;
  }> = ({ item, index, editing, handleRemoveLocation }) => (
    <List.Item>
      <Card className="bg-[#FBFBFF]">
        <List.Item.Meta
          avatar={
            <div className="p-1">
              {" "}
              <FolderLock />
            </div>
          }
          title={
            <div className="flex  flex-col-reverse md:flex-row-reverse justify-between  gap-2">
              <div>
                <span className="text-color-gray  font-normal ">
                  {intl.formatMessage({
                    id: "permissionType",
                    defaultMessage: "نوع الصلاحية",
                  })}
                  :
                </span>
                <span className="px-1">
                  {getFormatedTeamMemberPermission(
                    item?.pivot?.permission,
                    currentLang
                  )}
                </span>
              </div>
              <div>
                <span className="text-color-gray text-base font-normal">
                  <LocationCheckSVG />{" "}
                  {intl.formatMessage({
                    id: "branch",
                    defaultMessage: "الفرع",
                  })}
                  :
                </span>
                <span className="px-1">{item?.name[currentLang]}</span>
              </div>
            </div>
          }
        />
      </Card>
    </List.Item>
  );
  const [editing, setEditing] = useState(false);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [form] = Form.useForm();

  const { mutate: editMutation, isLoading } = useMutation<
    void,
    unknown,
    { location_permissions: { location_id: number; permission: string }[] }
  >((params) => editTeamMember(teamMemberData?.id, params), {
    onSuccess: (res) => {
      message.success("Edit success!");
      queryClient.invalidateQueries([queryKeys.listSpacficTeamMemer]);
      setEditing(false);
      setShowLocationSelector(false);
      setSelectedLocationIds([]);
    },
    onError: (error) => {
      message.error(
        intl.formatMessage({ id: "tryAgain", defaultMessage: "حاول مرة اخرى" })
      );
    },
  });

  const handleRemoveLocation = (locationId: number) => {
    if (
      teamMemberData?.locations.some(
        (loc: { id: number }) => loc.id === locationId
      )
    ) {
      const updatedLocations = teamMemberData?.locations.filter(
        (loc: { id: number }) => loc.id !== locationId
      );
      const updatedLocationIds = updatedLocations.map(
        (loc: { id: number }) => loc.id
      );
      editMutation({
        location_permissions: updatedLocationIds.map((location_id: any) => ({
          location_id,
        })),
      });
    } else {
      handleRemoveLocalLocation(locationId);
    }
  };

  const handleRemoveLocalLocation = (locationId: number) => {
    setSelectedLocationIds((prevIds) =>
      prevIds.filter((id) => id !== locationId)
    );
  };

  const handleAddLocationClick = () => {
    setShowLocationSelector(true);
  };

  const { data: listLocations } = useQuery([queryKeys.listLocations], () =>
    getAllLocations()
  );

  const onFinish = async (values: any) => {
    editMutation(values);
  };

  return (
    <div className="tabs bg-[#EEF0FF] p-4">
      <Flex justify="flex-end" className="mt-2 md:mt-0">
        <Button
          onClick={() => setEditing(!editing)}
          className="border-none bg-transparent shadow-none outline-none"
        >
          <EditSVG />
        </Button>
      </Flex>

      <div className="text-base font-normal leading-7">
        {intl.formatMessage({
          id: "memberPermissions",
          defaultMessage: "صلاحيات العضو",
        })}
      </div>

      {!editing ? (
        <>
          <List
            grid={{
              gutter: 14,
              xs: 1,
              sm: 1,
              md: 1,
              lg: 1,
              xl: 1,
              xxl: 1,
            }}
            className="mt-4"
            dataSource={[
              ...(teamMemberData?.locations || []),
              ...(listLocations?.data || []).filter((location: Location) =>
                selectedLocationIds.includes(location.id)
              ),
            ]}
            renderItem={(
              item: Location & { name: { ar: string; en: string } },
              index
            ) => (
              <>
                <PermissionCard
                  key={item.id}
                  item={item}
                  index={index}
                  editing={editing}
                  handleRemoveLocation={handleRemoveLocation}
                />
              </>
            )}
          />

          <div className="text-center my-5">
            <Button
              type="primary"
              className="h-full bg-[#FBFBFF]  text-black"
              icon={<SettingsSVG />}
              onClick={() => setEditing(!editing)}
            >
              {intl.formatMessage({
                id: "permissionSettings",
                defaultMessage: "اعدادات الصلاحيات",
              })}
            </Button>
          </div>
        </>
      ) : (
        <div>
          <Form
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              location_permissions: (teamMemberData?.locations || []).map(
                (location: Location) => ({
                  location_id: location.id,
                  permission: location.pivot.permission,
                })
              ),
            }}
            form={form}
          >
            <Form.List name="location_permissions">
              {(fields, { add, remove }) => (
                <>
                  <Form.Item className="p-5 rounded bg-[#FBFBFF] w-full text-color-primary">
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Flex wrap="wrap" justify="space-between">
                        <div className="flex gap-2 flex-col md:flex-row justify-between ">
                          <Form.Item>
                            {index === 0
                              ? intl.formatMessage({
                                  id: "basicBranchForMember",
                                  defaultMessage: "الفرع الاساسي للعضو",
                                })
                              : intl.formatMessage({
                                  id: "additionalBranchForMember",
                                  defaultMessage: "فرع اضافي للعضو ",
                                })}
                          </Form.Item>
                          <div>
                            <Form.Item
                              {...restField}
                              required={false}
                              className="px-3"
                              name={[name, "location_id"]}
                              rules={[
                                { required: true, message: "اختر الفرع" },
                              ]}
                            >
                              <Select
                                placeholder="اختر الفرع"
                                className="bg-color-light rounded-md p-2 w-fit "
                                bordered={false}
                                options={listLocations?.data?.map(
                                  (item: Location) => ({
                                    value: item.id,
                                    label: (
                                      <div className="flex gap-2 justify-between items-center">
                                        <p>{item.name}</p>
                                        <SalonSVG />
                                      </div>
                                    ),
                                  })
                                )}
                                style={{
                                  position: "relative",
                                  paddingRight: "10px",
                                }}
                              />
                            </Form.Item>
                          </div>
                        </div>
                        <Flex wrap="wrap" className="">
                          <Form.Item className="">
                            {intl.formatMessage({
                              id: "memberPermissionOnBranch",
                              defaultMessage: "صلاحيات العضو في الفرع",
                            })}{" "}
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            className="px-3  "
                            required={false}
                            name={[name, "permission"]}
                            rules={[
                              {
                                required: true,
                                message: "أدخل صلاحيات العضو في الفرع",
                              },
                            ]}
                          >
                            <Select
                              placeholder={intl.formatMessage({
                                id: "selectMemberPermissionOnBranch",
                                defaultMessage: "اختر صلاحيات العضو في الفرع",
                              })}
                              className="min-w-[170px]  my-input"
                              options={localizedTeamMemberPermission?.map(
                                (item) => ({
                                  value: item?.value,
                                  label: item?.label[currentLang],
                                })
                              )}
                              suffixIcon={
                                <div className="ms-4">
                                  <SalonSVG />
                                  <DownOutlined />
                                </div>
                              }
                            />
                          </Form.Item>
                          {/* <div>
                            {index === fields.length - 1 && (
                              <Button
                                className="border-none shadow-none outline-none"
                                onClick={() => {
                                  add();
                                }}
                                block
                                icon={
                                  <PlusOutlined className="bg-[#00086D] text-white p-1 rounded-md" />
                                }
                              />
                            )}
                          </div> */}
                        </Flex>
                      </Flex>
                    ))}
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Flex gap="small" justify="flex-end">
              <Button
                type="primary"
                loading={isLoading}
                className="text-white w-[164.5px]"
                htmlType="submit"
              >
                {intl.formatMessage({
                  id: "saveChanges",
                  defaultMessage: "حفظ التغييرات",
                })}
              </Button>
              <Button
                type="primary"
                onClick={() => setEditing(!editing)}
                className="bg-[#FBFBFF] text-black w-[164.5px]"
                htmlType="button"
              >
                {intl.formatMessage({
                  id: "cancel",
                  defaultMessage: "إلفاء",
                })}
              </Button>
            </Flex>
          </Form>
        </div>
      )}
    </div>
  );
};

export default Permission;
